/**
 * 매출 페이지(/owner/analytics) 비밀번호 잠금 게이트.
 *
 * - 매출 전용 비밀번호(로그인 비번과 분리)가 설정돼 있으면, 맞게 입력해야 매출이 보임.
 * - 비번이 설정되기 전엔 지금처럼 보임 + '매출 잠금 설정' 버튼 노출(오너만).
 * - 화면을 떠나면(언마운트) 다시 잠김 — 이 컴포넌트 state 로만 열림 상태 유지(저장 안 함).
 * - 잠금 통과 전엔 children(매출 페이지)을 렌더하지 않음 → 매출 데이터 자체를 안 불러옴.
 *
 * 백엔드: /v1/owner/revenue-lock/{status,verify,set,disable}
 */
import React, { useState, useEffect, useCallback } from 'react';

type Phase = 'loading' | 'error' | 'open' | 'locked' | 'unlocked';
type Modal = null | 'setup' | 'change' | 'disable';

async function client() {
  const { apiClient } = await import('../../../infrastructure/api');
  return apiClient;
}

function errMsg(e: any, fallback: string): string {
  return e?.response?.data?.detail || fallback;
}

const RevenueLockGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [pw, setPw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState<Modal>(null);

  const loadStatus = useCallback(async () => {
    setPhase('loading');
    try {
      const c = await client();
      const res = await c.get('/v1/owner/revenue-lock/status');
      setPhase(res.data?.is_set ? 'locked' : 'open');
    } catch {
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const submitVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const c = await client();
      await c.post('/v1/owner/revenue-lock/verify', { password: pw });
      setPw('');
      setPhase('unlocked');
    } catch (e: any) {
      setError(errMsg(e, '비밀번호가 올바르지 않습니다.'));
    } finally {
      setBusy(false);
    }
  };

  // ---- 잠금 안내/비번 입력 화면 ----
  if (phase === 'loading') {
    return <div className="p-10 text-center text-gray-400">확인 중...</div>;
  }

  if (phase === 'error') {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <p className="text-gray-500 mb-4">매출 잠금 상태를 확인할 수 없습니다.</p>
        <button onClick={loadStatus} className="px-4 py-2 bg-slate-800 text-white rounded-lg">
          다시 시도
        </button>
      </div>
    );
  }

  if (phase === 'locked') {
    return (
      <div className="max-w-md mx-auto mt-16">
        <div className="bg-white rounded-2xl shadow p-8 text-center">
          <div className="text-4xl mb-3">🔒</div>
          <h2 className="text-lg font-bold text-gray-900">매출 보기</h2>
          <p className="mt-1 text-sm text-gray-500">
            직원이 매출을 볼 수 없도록 잠겨 있어요.<br />매출 비밀번호를 입력하세요.
          </p>
          <form onSubmit={submitVerify} className="mt-6 space-y-3">
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="매출 비밀번호"
              className="w-full px-4 py-3 border rounded-lg text-center tracking-widest"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={busy || !pw}
              className="w-full py-3 bg-slate-800 hover:bg-slate-900 text-white rounded-lg disabled:bg-slate-300"
            >
              {busy ? '확인 중...' : '매출 보기'}
            </button>
          </form>
          <p className="mt-4 text-xs text-gray-400">
            비밀번호를 잊으셨나요? 본부에 초기화를 요청하세요.
          </p>
        </div>
      </div>
    );
  }

  // ---- open(잠금 미설정) 또는 unlocked(잠금 통과) → 매출 표시 + 상단 잠금 컨트롤 ----
  const isSet = phase === 'unlocked';
  return (
    <div>
      <div
        className={`flex items-center justify-between px-4 py-2 mb-3 rounded-lg text-sm ${
          isSet ? 'bg-slate-100 text-slate-600' : 'bg-amber-50 text-amber-800 border border-amber-200'
        }`}
      >
        <span className="flex items-center gap-1.5">
          {isSet ? '🔒 매출 잠금 켜짐 (직원은 매출을 볼 수 없어요)' : '🔓 매출이 직원에게도 보입니다'}
        </span>
        <span className="flex gap-2">
          {isSet ? (
            <>
              <button onClick={() => setModal('change')} className="underline hover:no-underline">
                비밀번호 변경
              </button>
              <button onClick={() => setModal('disable')} className="underline hover:no-underline">
                잠금 해제
              </button>
            </>
          ) : (
            <button
              onClick={() => setModal('setup')}
              className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-md font-medium"
            >
              🔒 매출 잠금 설정
            </button>
          )}
        </span>
      </div>

      {children}

      {modal && (
        <LockFormModal
          mode={modal}
          onClose={() => setModal(null)}
          onDone={(next) => {
            setModal(null);
            // 설정/변경 후엔 계속 보이게(unlocked), 해제 후엔 열림(open)
            setPhase(next);
          }}
        />
      )}
    </div>
  );
};

// ---- 설정/변경/해제 모달 ----
const LockFormModal: React.FC<{
  mode: 'setup' | 'change' | 'disable';
  onClose: () => void;
  onDone: (next: Phase) => void;
}> = ({ mode, onClose, onDone }) => {
  const [current, setCurrent] = useState('');
  const [next1, setNext1] = useState('');
  const [next2, setNext2] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const title = mode === 'setup' ? '매출 잠금 설정' : mode === 'change' ? '매출 비밀번호 변경' : '매출 잠금 해제';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (mode !== 'disable') {
      if (next1.length < 4) return setError('비밀번호는 4자 이상이어야 합니다.');
      if (next1 !== next2) return setError('새 비밀번호가 서로 다릅니다.');
    }
    setBusy(true);
    try {
      const c = await client();
      if (mode === 'disable') {
        await c.post('/v1/owner/revenue-lock/disable', { current_password: current });
        onDone('open');
      } else {
        await c.post('/v1/owner/revenue-lock/set', {
          new_password: next1,
          current_password: mode === 'change' ? current : undefined,
        });
        onDone('unlocked');
      }
    } catch (e: any) {
      setError(errMsg(e, '처리에 실패했습니다.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
        <p className="text-xs text-gray-500 mb-4">
          {mode === 'setup'
            ? '이 비밀번호를 아는 사람만 매출을 볼 수 있어요. 직원에게는 알려주지 마세요.'
            : mode === 'change'
              ? '현재 비밀번호 확인 후 새 비밀번호로 바꿉니다.'
              : '해제하면 직원도 매출을 다시 볼 수 있게 됩니다.'}
        </p>
        <form onSubmit={submit} className="space-y-3">
          {(mode === 'change' || mode === 'disable') && (
            <input
              type="password" inputMode="numeric" autoFocus value={current}
              onChange={(e) => setCurrent(e.target.value)} placeholder="현재 비밀번호"
              className="w-full px-3 py-2 border rounded-lg"
            />
          )}
          {mode !== 'disable' && (
            <>
              <input
                type="password" inputMode="numeric" autoFocus={mode === 'setup'} value={next1}
                onChange={(e) => setNext1(e.target.value)} placeholder="새 비밀번호 (4자 이상)"
                className="w-full px-3 py-2 border rounded-lg"
              />
              <input
                type="password" inputMode="numeric" value={next2}
                onChange={(e) => setNext2(e.target.value)} placeholder="새 비밀번호 확인"
                className="w-full px-3 py-2 border rounded-lg"
              />
            </>
          )}
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
              취소
            </button>
            <button
              type="submit" disabled={busy}
              className={`flex-1 py-2 text-white rounded-lg disabled:bg-slate-300 ${
                mode === 'disable' ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-800 hover:bg-slate-900'
              }`}
            >
              {busy ? '처리 중...' : mode === 'disable' ? '잠금 해제' : '저장'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RevenueLockGate;
